import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslation } from '@kostbar/i18n'
import { CheckCircle2, XCircle } from 'lucide-react'
import { todayIso } from '../lib/scheduling'
import type { RestaurantScheduling } from '@/api/types'
import { restaurantsApi } from '@/api/endpoints/restaurants'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface Props {
  restaurantId: number
  scheduling: RestaurantScheduling
}

/**
 * Runs the current (unsaved) config through the server's schedule resolver, so what the
 * operator previews is exactly what enforcement will do — the logic is never duplicated here.
 */
export function SchedulingPreviewPanel({ restaurantId, scheduling }: Props) {
  const { t: tRestaurants } = useTranslation('restaurants')
  const [date, setDate] = useState(todayIso())
  const [nowHm, setNowHm] = useState('10:00')

  const query = useQuery({
    queryKey: [
      'schedulingPreview',
      restaurantId,
      date,
      nowHm,
      JSON.stringify(scheduling),
    ],
    queryFn: () =>
      restaurantsApi.previewScheduling(restaurantId, {
        scheduling,
        date,
        nowLocal: nowHm,
      }),
    enabled: Boolean(date) && /^\d{2}:\d{2}$/.test(nowHm),
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  })

  const preview = query.data
  const windowLabel = (w: {
    closed: boolean
    open?: string
    close?: string
  }) =>
    w.closed
      ? tRestaurants('scheduling.previewPanel.closedAllDay')
      : `${w.open}–${w.close}`

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tRestaurants('scheduling.previewPanel.title')}</CardTitle>
        <CardDescription>
          {tRestaurants('scheduling.previewPanel.hint')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label>{tRestaurants('scheduling.previewPanel.date')}</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="space-y-2">
            <Label>{tRestaurants('scheduling.previewPanel.now')}</Label>
            <Input
              type="time"
              value={nowHm}
              onChange={(e) => setNowHm(e.target.value)}
              className="w-32"
            />
          </div>
          {preview?.exceptionApplied && (
            <Badge variant="secondary" className="mb-1.5">
              {tRestaurants('scheduling.previewPanel.exception')}
            </Badge>
          )}
        </div>

        {query.isError && (
          <p className="text-sm text-destructive">
            {tRestaurants('scheduling.previewPanel.invalid')}
          </p>
        )}
        {query.isPending && (
          <div className="space-y-2">
            <Skeleton className="h-6 w-64" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {preview && !query.isError && (
          <>
            <div className="flex items-center gap-2">
              {preview.now.orderable ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="font-medium">
                    {tRestaurants('scheduling.previewPanel.orderable')}
                  </span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-destructive" />
                  <span className="font-medium">
                    {tRestaurants('scheduling.previewPanel.notOrderable')}
                  </span>
                  {preview.now.reasonCode && (
                    <span className="text-sm text-muted-foreground">
                      {tRestaurants(
                        `scheduling.previewPanel.reason.${preview.now.reasonCode}` as const,
                      )}
                    </span>
                  )}
                </>
              )}
            </div>

            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm md:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">
                  {tRestaurants('scheduling.previewPanel.minAdvance')}
                </dt>
                <dd className="font-medium">
                  {tRestaurants('scheduling.previewPanel.minutes', {
                    count: preview.now.minAdvanceMinutes,
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">
                  {tRestaurants('scheduling.previewPanel.earliestPickup')}
                </dt>
                <dd className="font-medium">
                  {preview.now.earliestPickupLocal ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">
                  {tRestaurants('scheduling.previewPanel.latestPickup')}
                </dt>
                <dd className="font-medium">
                  {preview.now.latestPickupLocal ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">
                  {tRestaurants('scheduling.previewPanel.lastOrderable')}
                </dt>
                <dd className="font-medium">
                  {preview.now.lastOrderableLocal ?? '—'}
                </dd>
              </div>
            </dl>

            <div>
              <div className="mb-2 text-sm font-medium">
                {tRestaurants('scheduling.previewPanel.windows')}
              </div>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm md:grid-cols-4">
                <div>
                  <dt className="text-muted-foreground">
                    {tRestaurants('scheduling.exceptions.window.unmanned')}
                  </dt>
                  <dd className="font-medium">
                    {windowLabel(preview.unmanned)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {tRestaurants('scheduling.exceptions.window.staffed')}
                  </dt>
                  <dd className="font-medium">
                    {windowLabel(preview.staffed)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {tRestaurants('scheduling.exceptions.window.ordering')}
                  </dt>
                  <dd className="font-medium">
                    {preview.ordering.closed
                      ? tRestaurants('scheduling.previewPanel.closedAllDay')
                      : `${preview.ordering.opensLocal}–${preview.ordering.closesLocal ?? '∞'}`}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {tRestaurants('scheduling.exceptions.window.pickup')}
                  </dt>
                  <dd className="font-medium">
                    {preview.pickup.closed
                      ? tRestaurants('scheduling.previewPanel.closedAllDay')
                      : `${preview.pickup.serviceStartLocal}–${preview.pickup.serviceEndLocal}`}
                  </dd>
                </div>
              </dl>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
